import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trophy, Crown, Medal, TrendingUp, Users } from "lucide-react";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;

const rankStyles = {
  1: { ring: "ring-2 ring-amber-400", chip: "bg-amber-400 text-amber-950", Icon: Crown },
  2: { ring: "ring-1 ring-slate-300", chip: "bg-slate-300 text-slate-800", Icon: Medal },
  3: { ring: "ring-1 ring-orange-300", chip: "bg-orange-300 text-orange-900", Icon: Medal },
};

export const PartnerLeaderboard = ({ compact = false }) => {
  const [leaders, setLeaders] = useState(null);

  useEffect(() => {
    api.get("/partners/leaderboard").then((r) => setLeaders(r.data.leaders || [])).catch(() => setLeaders([]));
  }, []);

  if (leaders === null) {
    return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Card className="p-5" data-testid="partner-leaderboard">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Trophy className="w-4 h-4" /></span>
        <h3 className="font-semibold">Top Earners</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Our highest-earning partners this season. Share more to climb the board!</p>

      {leaders.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="leaderboard-empty">
          No partners on the board yet — be the first to start earning!
        </p>
      ) : (
        <div className="space-y-1.5">
          {leaders.slice(0, compact ? 5 : 20).map((l) => {
            const st = rankStyles[l.rank];
            return (
              <div key={l.rank} data-testid={`leader-row-${l.rank}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 bg-muted/40 ${st ? st.ring : ""}`}>
                <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-black ${st ? st.chip : "bg-muted text-muted-foreground"}`}>
                  {st ? <st.Icon className="w-4 h-4" /> : l.rank}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{l.name}</span>
                    <Badge variant="outline" className="capitalize text-[10px] px-1.5 py-0">{l.type}</Badge>
                  </div>
                  {!compact && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{l.signups} sign-ups</span>
                      <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />{l.sales_count} sales</span>
                    </div>
                  )}
                </div>
                <span className="font-black font-display text-green-600 shrink-0">{money(l.earnings)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
